import React, { useEffect, useCallback, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useNavigate } from "react-router-dom";

import BeamLogo from "../assets/beam-logo-black-period.png";
import BackgroundGradient from "../assets/beam-gradient.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faNetworkWired,
  faArrowsLeftRight,
  faUserPlus,
  faBolt,
  faShieldHalved,
  faClipboardCheck,
} from "@fortawesome/free-solid-svg-icons";
import API from "../api/api";

export default function Login() {
  const { login, authenticated, user } = usePrivy();
  const navigate = useNavigate();
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  const checkBusinessLeadStatus = useCallback(async () => {
    try {
      setIsCheckingStatus(true);
      const response = await API.get(
        `/business-lead/status?email=${encodeURIComponent(user.email.address)}`,
      );
      const data = response.data;
      console.log("[checkBusinessLeadStatus] data:", data);
      if (data.skipOnboarding) {
        if (data.businessId) {
          navigate(`/accounts/${data.businessId}`);
        } else if (data.isRejected) {
          navigate("/accounts/rejected");
        } else {
          navigate("/accounts");
        }
      } else if (!data.exists) {
        navigate("/business-onboarding");
      } else if (!data.isComplete) {
        navigate("/business-onboarding", {
          state: { startAtStep: data.lastCompletedStep },
        });
      } else {
        navigate("/accounts");
      }
    } catch (error) {
      console.error("Error checking business lead status:", error);
      setIsCheckingStatus(false);
    }
  }, [navigate, user?.email]);

  useEffect(() => {
    if (authenticated && user?.email) {
      checkBusinessLeadStatus();
    }
  }, [authenticated, user, checkBusinessLeadStatus]);

  const features = [
    { icon: faNetworkWired, text: "Comprehensive payment network access" },
    { icon: faArrowsLeftRight, text: "Wider transaction margins" },
    { icon: faUserPlus, text: "Flexible onboarding options" },
    { icon: faBolt, text: "Quicker access to funds" },
    { icon: faShieldHalved, text: "Regulated & FinCEN certified" },
    { icon: faClipboardCheck, text: "Robust payments compliance" },
  ];

  if (authenticated && isCheckingStatus) {
    // Show loading while checking status to prevent showing onboarding prematurely
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <img src={BeamLogo} alt="Beam" className="h-8 mb-4 mx-auto" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // If authenticated but not checking status, this means navigation failed
  // or they should be on a different route - redirect to business onboarding as fallback
  if (authenticated && !isCheckingStatus) {
    navigate("/business-onboarding");
    return null;
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center pt-24 px-6 relative"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.7)), url(${BackgroundGradient})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="w-full max-w-2xl text-center mb-16">
        <img src={BeamLogo} alt="Beam" className="h-8 mb-4 mx-auto" />
        <h3 className="text-gray-600 text-[17px] font-normal leading-relaxed max-w-xl mx-auto">
          Send and receive money with unmatched speed, savings, and security —
          powered by Beam's breakthrough stablecoin and fiat solutions.
        </h3>
      </div>

      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div className="py-12 lg:pt-16">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 mb-8 last:mb-0">
              <div className="w-5 h-5 flex items-center justify-center opacity-50">
                <FontAwesomeIcon
                  icon={feature.icon}
                  className="text-[#6333A4] text-lg"
                />
              </div>
              <span className="text-[#2B2D33] font-medium text-[17px]">
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-b from-white/100 to-white/40 backdrop-blur-sm rounded-md px-10 py-28 shadow-sm min-w-[480px] mx-auto">
          <h2 className="text-2xl font-medium mb-10 text-center">
            Login or signup
          </h2>

          <div className="mb-10">
            <div className="relative flex justify-center">
              <button
                onClick={() => login()}
                className="transform -translate-y-1/2 px-8 py-1.5 bg-gray-50 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Continue with Email
              </button>
              {/* <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-[#6333A4] text-gray-800 placeholder-gray-400 text-sm bg-white"
              />
              <button
                onClick={() => login()}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-gray-50 text-gray-600 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Submit
              </button> */}
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By logging in I agree to the{" "}
            <a
              href="/terms"
              className="text-[#6333A4] hover:text-[#522c86] transition-colors"
            >
              Terms
            </a>
            {" & "}
            <a
              href="/privacy"
              className="text-[#6333A4] hover:text-[#522c86] transition-colors"
            >
              Privacy Policy
            </a>
          </p>
          <p className="text-xs text-center mt-1.5">
            <a
              href="https://privy.io"
              className="text-[#6333A4] hover:text-[#522c86] transition-colors"
            >
              Protected by Privy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
